/**
 * ChartAreaInteractive Component
 *
 * Interactive area chart for dashboard metrics visualization
 * Features:
 * - 4 metrics: visitors, revenue, newCustomers, repeatCustomers
 * - Time range selection: 7d, 30d, 90d
 * - shadcn/ui design system compliant
 * - Responsive and animated
 *
 * Design Document: docs/architecture/dashboard-shadcn-migration-design.md
 */

import { useMemo, useState } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis } from 'recharts';
import { TrendingUp } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ChartDataPoint } from '@/hooks/useDashboardData';

// チャート設定（デザインシステムの色を使用）
const chartConfig = {
  visitors: {
    label: '来店数',
    color: 'var(--chart-2)', // シアン系 - 来店数（流入）
  },
  revenue: {
    label: '売上',
    color: 'var(--chart-4)', // イエローグリーン系 - 売上（成長）
  },
  newCustomers: {
    label: '新規来店数',
    color: 'var(--chart-1)', // オレンジ系 - 新規（活発）
  },
  repeatCustomers: {
    label: 'リピーター来店数',
    color: 'var(--chart-3)', // ブルー系 - リピーター（安定）
  },
} satisfies ChartConfig;

// メトリクスタイプ
type MetricType = 'visitors' | 'revenue' | 'newCustomers' | 'repeatCustomers';

// 時間範囲タイプ
type TimeRange = '7d' | '30d' | '90d';

interface ChartAreaInteractiveProps {
  data: ChartDataPoint[];
  loading?: boolean;
}

/**
 * チャートエリアインタラクティブコンポーネント
 */
export function ChartAreaInteractive({ data, loading = false }: ChartAreaInteractiveProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('visitors');

  // 時間範囲に応じたデータフィルタリング
  const filteredData = useMemo(() => {
    const daysMap = {
      '7d': 7,
      '30d': 30,
      '90d': 90,
    };
    const days = daysMap[timeRange];
    return data.slice(-days);
  }, [data, timeRange]);

  // メトリクスのラベルマップ
  const metricLabels: Record<MetricType, string> = {
    visitors: '来店数',
    revenue: '売上',
    newCustomers: '新規来店数',
    repeatCustomers: 'リピーター来店数',
  };

  // 売上の場合は通貨フォーマット
  const formatValue = (value: number): string => {
    if (selectedMetric === 'revenue') {
      return `¥${value.toLocaleString()}`;
    }
    return `${value}`;
  };

  // トレンド計算（直近7日 vs 前の7日）
  const trend = useMemo(() => {
    if (filteredData.length < 14) return { value: 0, direction: 'neutral' as const };

    const recent7Days = filteredData.slice(-7);
    const previous7Days = filteredData.slice(-14, -7);

    const recentSum = recent7Days.reduce((sum, d) => sum + d[selectedMetric], 0);
    const previousSum = previous7Days.reduce((sum, d) => sum + d[selectedMetric], 0);

    if (previousSum === 0) return { value: 0, direction: 'neutral' as const };

    const percentChange = ((recentSum - previousSum) / previousSum) * 100;
    const direction = percentChange > 0 ? 'up' : percentChange < 0 ? 'down' : 'neutral';

    return { value: Math.abs(percentChange), direction };
  }, [filteredData, selectedMetric]);

  // 合計値計算
  const totalValue = useMemo(() => {
    return filteredData.reduce((sum, d) => sum + d[selectedMetric], 0);
  }, [filteredData, selectedMetric]);

  // 日付フォーマット（月/日）
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  return (
    <Card>
      <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
          <CardTitle>{metricLabels[selectedMetric]}の推移</CardTitle>
          <CardDescription>
            {timeRange === '7d' && '過去7日間'}
            {timeRange === '30d' && '過去30日間'}
            {timeRange === '90d' && '過去90日間'}
            のデータを表示
          </CardDescription>
        </div>
        <div className="flex gap-2 px-6 py-4 pb-6 sm:pb-4">
          {/* メトリクス選択 */}
          <Select
            value={selectedMetric}
            onValueChange={(value) => setSelectedMetric(value as MetricType)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="visitors">来店数</SelectItem>
              <SelectItem value="revenue">売上</SelectItem>
              <SelectItem value="newCustomers">新規来店数</SelectItem>
              <SelectItem value="repeatCustomers">リピーター来店数</SelectItem>
            </SelectContent>
          </Select>

          {/* 時間範囲選択 */}
          <Select
            value={timeRange}
            onValueChange={(value) => setTimeRange(value as TimeRange)}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">7日間</SelectItem>
              <SelectItem value="30d">30日間</SelectItem>
              <SelectItem value="90d">90日間</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="px-2 sm:p-6">
        {loading ? (
          <div className="flex h-[250px] items-center justify-center text-muted-foreground">
            データを読み込み中...
          </div>
        ) : filteredData.length === 0 ? (
          <div className="flex h-[250px] items-center justify-center text-muted-foreground">
            データがありません
          </div>
        ) : (
          <>
            {/* サマリー情報 */}
            <div className="mb-4 mt-4 sm:mt-0 flex items-center justify-between px-4">
              <div>
                <p className="text-sm text-muted-foreground">合計</p>
                <p className="text-2xl font-bold">{formatValue(totalValue)}</p>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <TrendingUp
                  className={`h-4 w-4 ${
                    trend.direction === 'up'
                      ? 'text-green-600'
                      : trend.direction === 'down'
                        ? 'text-red-600'
                        : 'text-gray-400'
                  }`}
                />
                <span
                  className={`font-semibold ${
                    trend.direction === 'up'
                      ? 'text-green-600'
                      : trend.direction === 'down'
                        ? 'text-red-600'
                        : 'text-gray-400'
                  }`}
                >
                  {trend.direction === 'up' ? '+' : trend.direction === 'down' ? '-' : ''}
                  {trend.value.toFixed(1)}%
                </span>
                <span className="text-muted-foreground">前週比</span>
              </div>
            </div>

            {/* チャート */}
            <ChartContainer
              config={chartConfig}
              className="aspect-auto h-[250px] w-full"
            >
              <AreaChart
                accessibilityLayer
                data={filteredData}
                margin={{
                  left: 12,
                  right: 12,
                }}
              >
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={32}
                  tickFormatter={formatDate}
                />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      labelFormatter={(value) => formatDate(value as string)}
                      formatter={(value) => formatValue(value as number)}
                      indicator="dot"
                    />
                  }
                />
                <Area
                  dataKey={selectedMetric}
                  type="natural"
                  fill={chartConfig[selectedMetric].color}
                  fillOpacity={0.4}
                  stroke={chartConfig[selectedMetric].color}
                  stackId="a"
                />
              </AreaChart>
            </ChartContainer>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default ChartAreaInteractive;
